import PropTypes from 'prop-types';

import {
  AgeFromDate,
  Alert,
  DateTimeFormat,
  Icon,
  IconUrgency,
  Span,
  utils as designUtils
} from '@febos-io/design';

import React from 'react';
import useTranslation from '../hooks/useTranslation';
import OdontogramReport from './OdontogramReport';

export function InfoExam({
  exam,
}) {
  const { t, } = useTranslation();
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    designUtils.success(t('Copied to clipboard'));
  };
  return (
    <>
      <div className="row">
        <div className="col-sm-7">
          <IconUrgency urgency={exam.urgency} icon="flag" fontSize={15} />
          <strong className="text-uppercase text-primary ms-1">{t(exam.name)}</strong>
        </div>
        <div className="col-sm-5 text-md-end">
          <div className="row">
            <div className="col">
              <Span title={t('Creation Date')}>
                <Icon icon="calendar-day" className="me-1" />
                <DateTimeFormat value={exam.createdAt} format="dd-MM-yyyy hh:mm" />
              </Span>
            </div>
          </div>
          <div className="row">
            <div className="col">
              <Span
                title={t('Exam Code')}
                onClick={() => copyToClipboard(exam.number + ' ' + exam.id)}
              >
                <Icon icon="rectangle-barcode" className="me-1" />
                {exam.number}
              </Span>
              {exam.caseAntecedent && (
                <Span
                  title={`${t('Associated with clinical study')} #${exam.caseAntecedent?.number}`}
                  className="badge bg-gray-500 ms-1"
                >
                  <Icon icon="memo-circle-info" className="me-1" />
                  {exam.caseAntecedent?.number}
                </Span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="row mt-2 border-bottom px-2">
        <div className="col-md-6">
          <Icon icon="user" className="me-1" />
          <strong>{t('Patient')}</strong>
          <p>
            {exam.patient?.name}
            {' '}
            {exam.patient?.lastName}
            {exam.patient?.motherLastName && ` ${exam.patient?.motherLastName}`}
          </p>
        </div>
        <div className="col-md-3">
          <Icon icon="calendar-day" className="me-1" />
          <strong>{t('Date of Birth')}</strong>
          <p>
            <DateTimeFormat value={exam.patient?.birthDate} format="dd-MM-yyyy" />
          </p>
        </div>
        <div className="col-md-3">
          <Icon icon="calendar-day" className="me-1" />
          <strong>{t('Age')}</strong>
          <p>
            <AgeFromDate value={exam.patient?.birthDate} isIso />
          </p>
        </div>
        <div className="col-md-6">
          <div className="mb-1">
            <strong className="text-gray">{t('Dentist')}</strong>
            <br />
            <Icon icon="user-doctor" className="me-2" />
            {'Dr(a). '}
            {exam.dentist?.name}
            {' '}
            {exam.dentist?.lastName}
          </div>
        </div>
        <div className="col-md-12">
          <Icon icon="stethoscope" className="me-1" />
          <strong>{t('Medical diagnosis')}</strong>
          <p>{exam.medicalDiagnosis}</p>
        </div>
        {exam.url && (
          <div className="col-md-12">
            <Icon icon="link" className="me-1" />
            <strong>{t('URL')}</strong>
            <p>{exam.url}</p>
          </div>
        )}
      </div>
      {(exam.diagnostic || exam.observations) && (
        <div className="row mt-2 border-bottom px-2">
          {exam.diagnostic && (
            <div className="col-md">
              <Icon icon="stethoscope" className="me-1" />
              <strong>{t('Diagnosis')}</strong>
              <p>{exam.diagnostic}</p>
            </div>
          )}
          {exam.observations && (
            <div className="col-md">
              <Icon icon="file-magnifying-glass" className="me-1" />
              <strong>{t('Observations')}</strong>
              <p>{exam.observations}</p>
            </div>
          )}
        </div>
      )}
      {exam.odontogram && exam.odontogram?.length > 0 && (
        <>
          <div className="row mt-2 px-2">
            <div className="col-12">
              <Icon icon="tooth" className="me-1" />
              <strong>{t('Teeth')}</strong>
            </div>
          </div>
          <div className="row mt-1 border-bottom px-2 pb-2">
            <OdontogramReport teeth={exam.odontogram} isChild={exam.isChild} isADA={exam.isADA} />
          </div>
        </>
      )}
      {exam.options && exam.options?.length > 0 && (
        <>
          <div className="row mt-2 px-2">
            <div className="col-12">
              <Alert color="danger">
                <Icon icon="tooth" className="me-1" />
                <strong>{t('Requested analyzes')}</strong>
              </Alert>
            </div>
          </div>
          <div className="row mt-1 border-bottom px-2 pb-2">
            <div className="col">
              {exam.options.map((option) => (
                <Span key={option} title={t(option)} className="badge bg-primary me-1 mb-1">
                  {option === 'Others' ? t(exam.optionsOther) : t(option)}
                </Span>
              ))}
            </div>
          </div>
        </>
      )}
      {exam.canceledReason && (
        <div className="row mt-2 px-2">
          <div className="col-12">
            <strong className="text-danger">
              <Icon icon="tooth" className="me-1" />
              {t('Cancellation reason report')}
            </strong>
            <Alert color="danger">
              <div className="row mt-1 border-bottom px-2 pb-2">
                <div className="col">
                  <p>{exam.canceledReason}</p>
                </div>
              </div>
            </Alert>
          </div>
        </div>
      )}
    </>
  );
}

InfoExam.propTypes = {
  exam: PropTypes.object,
};

export default InfoExam;
